import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ChevronDown, Loader2, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/logo";
import { CONTACT_EMAIL } from "@/lib/seo/site";
import { WHATSAPP_CONTACT_NAME, WHATSAPP_CONTACT_PHONE, buildWhatsAppUrl } from "@/lib/whatsapp-contact";
import { LanguageSwitcher } from "@/components/language-switcher";
import { cn } from "@/lib/utils";
import { BeLeadSignature } from "@/components/BeLeadSignature";

function Newsletter() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const r = await fetch("/api/public/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, kind: "newsletter", source: "footer-mobile", website,
          locale: i18n.resolvedLanguage?.startsWith("en") ? "en" : "it" }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "Error");
      setDone(true);
    } catch (e: any) { setError(e?.message ?? "Error"); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="inline-flex items-center gap-2 text-[13px] text-emerald-300">
      <CheckCircle2 className="h-4 w-4" /> {t("footer.confirmed")}
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-2">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder={t("footer.emailPlaceholder")}
        className="h-11 w-full rounded-xl border mob-hairline-strong bg-white/5 px-3 text-[14px] text-white placeholder:text-white/40 outline-none focus:border-white/40" />
      <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />
      <button type="submit" disabled={loading}
        className="mob-tap inline-flex h-11 w-full items-center justify-center rounded-xl bg-white text-[14px] font-semibold text-black disabled:opacity-50">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("footer.subscribe")}
      </button>
      {error && <p className="text-[11px] text-red-400">{error}</p>}
    </form>
  );
}

function Collapsible({ index, title, children }: { index: string; title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b mob-hairline">
      <button onClick={() => setOpen((v) => !v)}
        className="mob-tap flex w-full items-center justify-between py-4 text-left text-white">
        <span className="flex items-center gap-2.5">
          <span className="font-editorial text-[12px] italic text-accent">{index}</span>
          <span className="h-px w-5 bg-white/20" aria-hidden />
          <span className="font-editorial text-[16px] font-semibold">{title}</span>
        </span>
        <ChevronDown className={cn("h-4 w-4 text-white/55 transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="pb-4 space-y-2.5">{children}</div>}
    </div>
  );
}

export function MobileFooter() {
  const { t } = useTranslation();
  const cols = [
    { title: t("footer.columns.product"), items: [
      { label: t("footer.links.features"), href: "/#features" },
      { label: t("footer.links.pricing"), href: "/#pricing" },
      { label: t("footer.links.demo"), href: "/#demo" },
      { label: t("footer.links.security"), href: "/sicurezza" },
    ]},
    { title: t("footer.columns.company"), items: [
      { label: t("footer.links.about"), href: "/chi-siamo" },
      { label: t("footer.links.blog"), href: "/blog" },
      { label: t("footer.links.contact"), href: "/contatti" },
      { label: t("footer.links.glossary"), href: "/glossario" },
    ]},
    { title: t("footer.columns.legal"), items: [
      { label: t("footer.links.privacy"), href: "/privacy" },
      { label: t("footer.links.terms"), href: "/termini" },
      { label: t("footer.links.refunds"), href: "/rimborsi" },
      { label: t("footer.links.gdpr"), href: "/gdpr" },
    ]},
  ];
  return (
    <footer className="mob-surface-0 border-t mob-hairline px-5 pt-10 pb-[calc(24px+env(safe-area-inset-bottom))] text-white">
      <Logo variant="light" size={36} />
      <p className="mt-4 max-w-[34ch] text-[13px] text-white/55">{t("footer.tagline")}</p>

      <div className="mt-6">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/55">{t("footer.newsletter")}</p>
        <Newsletter />
      </div>

      <div className="mt-8">
        {cols.map((c, ci) => (
          <Collapsible key={c.title} index={String(ci + 1).padStart(2, "0")} title={c.title}>
            {c.items.map((it) => (
              <a key={it.label} href={it.href} className="mob-tap block text-[13px] text-white/65 hover:text-white">{it.label}</a>
            ))}
          </Collapsible>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <LanguageSwitcher variant="dark" />
        <Link to="/login" className="mob-tap text-[13px] text-white/65 hover:text-white">{t("footer.login")} →</Link>
      </div>

      <p className="mt-6 text-[11px] text-white/65">
        <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white">{CONTACT_EMAIL}</a>
      </p>
      <p className="mt-2 text-[11px] text-white/65">
        {t("footer.whatsapp")}: <a href={buildWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="hover:text-white">{WHATSAPP_CONTACT_NAME} · {WHATSAPP_CONTACT_PHONE}</a>
      </p>
      <p className="mt-2 text-[11px] text-white/65">LexAIPro © {new Date().getFullYear()}</p>
      <div className="mt-3 flex justify-center">
        <BeLeadSignature tone="onDark" className="text-[10px]" />
      </div>
    </footer>
  );
}
